/**
 * Admin-Einstellungen → Matching: Gewichte der Vergleichsfelder und
 * Standard-Anzahl der Kandidaten je Liste.
 *
 *   GET    /v2/admin/matching-settings
 *   PUT    /v2/admin/matching-settings   { weights?, default_limit? } — Teil-Update
 *   DELETE /v2/admin/matching-settings   → zurück auf die Defaults
 *
 * Gewicht 0 = Feld wird ignoriert, 1 = Standard, höher = zählt stärker.
 * Stufenfelder (`fit`): Mindestanforderung, BK erreicht oder übertrifft die
 * Stufe der PB = 1, sonst 0 (kein Abzug für Überqualifikation, 29.10.2026).
 * Änderungen wirken sofort im Matching (Backend: MatchingSettings).
 */
import { apiV2 } from './api';

export type MatchingWeights = Record<string, number>;

export interface MatchingFieldDef {
  name: string;
  /** Vergleichstyp des Backends (equalOrBigger, includes, …). */
  type: string;
  /** Stufenfeld (Mindestanforderung: BK-Stufe ≥ PB-Stufe). */
  fit?: boolean;
}

export interface MatchingSettings {
  weights: MatchingWeights;
  default_limit: number;
  fields: MatchingFieldDef[];
  defaults: { weights: MatchingWeights; default_limit: number };
  limits: { weight_min: number; weight_max: number; limit_min: number; limit_max: number };
}

export interface MatchingSettingsUpdate {
  weights?: MatchingWeights;
  default_limit?: number;
}

const unwrap = (res: { data: { data: MatchingSettings } }): MatchingSettings => res.data.data;

export const matchingSettingsApi = {
  get: async (): Promise<MatchingSettings> =>
    unwrap(await apiV2.get<{ data: MatchingSettings }>('/admin/matching-settings')),

  update: async (payload: MatchingSettingsUpdate): Promise<MatchingSettings> =>
    unwrap(await apiV2.put<{ data: MatchingSettings }>('/admin/matching-settings', payload)),

  reset: async (): Promise<MatchingSettings> =>
    unwrap(await apiV2.delete<{ data: MatchingSettings }>('/admin/matching-settings')),
};

/** Kurzbeschreibung der Vergleichstypen (i18n-Schlüssel). */
export const MATCHING_TYPE_KEY: Record<string, string> = {
  equalOrBigger: 'settings.matching.type.equal_or_bigger',
  equalOrSmaller: 'settings.matching.type.equal_or_smaller',
  includes: 'settings.matching.type.includes',
  includesBK: 'settings.matching.type.includes_bk',
  pets: 'settings.matching.type.pets',
  sex: 'settings.matching.type.sex',
  noOrSame: 'settings.matching.type.no_or_same',
  smoker: 'settings.matching.type.smoker',
};
